import React, { useCallback, useEffect, useRef, useState } from 'react';
import { maakMeldingDocumentToken } from '../api/api';
import { kopieerPlat, kopieerRijk, MailVoorbeeld } from './PubliekeLinkModal';
import { leesbareFout } from '../util/format';

/* Het Melding-document: een PDF-weergave van wat er over deze melding in het
 * register staat, in dezelfde opmaak als de bijlage die een melder zelf uit
 * het Meldingsformulier haalt. Een paneel onder Correspondentie, naast de
 * publieke pagina; het venster eromheen zit in CorrespondentieModal.
 *
 * Waarom het register de PDF niet zelf bouwt:
 *   De generator staat in het Meldingsformulier -- pdfmake-documenten, de
 *   offscreen OpenLayers-kaarten, het logo, de regelteksten. Dat is ruim
 *   dertienhonderd regels. Een tweede exemplaar hier zou op de dag van
 *   oplevering gelijk zijn en daarna niet meer, en het verschil zou pas
 *   opvallen als een melder twee documenten naast elkaar legt.
 *
 * Hoe het dan wel gaat:
 *   1. Kan het? Dat is al beantwoord: CorrespondentieModal heeft de gegevens
 *      opgehaald en geeft ze hier door. Zo niet, dan staat hier waarom.
 *   2. Een eenmalig token, tien minuten geldig, aan deze inrichting gebonden.
 *   3. Een verborgen iframe naar de generator, met het token in de hash.
 *      Die haalt de gegevens op, bouwt het document en stuurt het terug.
 *   4. De PDF komt binnen als bytes en wordt hier een blob om te downloaden.
 *
 * Mailen kan het register niet: er staan geen NAW-gegevens in. Het adres van
 * de melder komt uit het zaaksysteem en de mail gaat vanuit Outlook. Vandaar
 * dat hier een bestand en een tekst staan, en geen verzendknop.
 */

// Ruim genomen: de generator rendert kaarttegels op 150 dpi en dat kan bij een
// inrichting met ver uit elkaar liggende putten oplopen. Loopt hij hierna nog,
// dan is er iets stuk en is wachten geen dienst meer.
const TIJDSLOT_MS = 180000;

function maat(bytes) {
  if (!bytes) return '';
  return bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1).replace('.', ',')} MB`
    : `${Math.round(bytes / 1024)} kB`;
}

function Voortgang({ fase, fractie }) {
  const pct = Math.max(2, Math.min(100, Math.round((fractie || 0) * 100)));
  return (
    <div className="meldoc-voortgang">
      <div className="meldoc-balk"><div className="meldoc-balk-vul" style={{ width: `${pct}%` }} /></div>
      <p className="meldoc-meta">
        {fase === 'document' ? 'Document samenstellen…' : 'Situatiekaart tekenen…'}
      </p>
    </div>
  );
}

export function MeldingDocumentPaneel({ inrichtingId, doc, onClose }) {
  const [fout, setFout] = useState(null);
  const [fase, setFase] = useState('kaart');
  const [fractie, setFractie] = useState(0);
  const [pdf, setPdf] = useState(null);          // { url, naam, bytes }
  const [tekst, setTekst] = useState(doc?.mailtekst || '');
  const [bewerken, setBewerken] = useState(false);
  const [gekopieerd, setGekopieerd] = useState(null);   // 'onderwerp' | 'mail' | 'mislukt'
  const frameRef = useRef(null);
  const pdfRef = useRef(null);
  const klokRef = useRef(null);

  // Alles wat buiten React leeft weer opruimen: het iframe, de blob-URL en de
  // klok. Zonder dit blijft een blob van een paar megabyte hangen tot de
  // gebruiker de hele app herlaadt.
  const ruimOp = useCallback(() => {
    if (klokRef.current) { clearTimeout(klokRef.current); klokRef.current = null; }
    if (frameRef.current) {
      try { frameRef.current.remove(); } catch (e) { /* al weg */ }
      frameRef.current = null;
    }
    if (pdfRef.current) {
      try { URL.revokeObjectURL(pdfRef.current); } catch (e) { /* al weg */ }
      pdfRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!doc || !doc.kan) return undefined;

    let levend = true;
    let herkomst = null;

    const opBericht = (e) => {
      // Alleen de generator mag hier iets zeggen. e.origin is door de pagina
      // zelf niet te vervalsen; dit is de kant die telt.
      if (!herkomst || e.origin !== herkomst) return;
      const b = e.data || {};
      if (!levend) return;
      if (b.type === 'melding-document-voortgang') {
        setFase(b.fase || 'kaart');
        setFractie(b.fractie || 0);
      } else if (b.type === 'melding-document-klaar') {
        const blob = new Blob([b.pdf], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        pdfRef.current = url;
        setPdf({ url, naam: b.bestandsnaam || 'Melding.pdf', bytes: blob.size });
        if (klokRef.current) { clearTimeout(klokRef.current); klokRef.current = null; }
        if (frameRef.current) { frameRef.current.remove(); frameRef.current = null; }
      } else if (b.type === 'melding-document-fout') {
        setFout(b.code === 'verlopen' || b.code === 'onbekend'
          ? 'De aanvraag voor dit document is verlopen. Sluit het venster en probeer het opnieuw.'
          : `Het document kon niet gebouwd worden. ${b.bericht || ''}`.trim());
        if (klokRef.current) { clearTimeout(klokRef.current); klokRef.current = null; }
      }
    };
    window.addEventListener('message', opBericht);

    (async () => {
      try {
        const t = await maakMeldingDocumentToken(inrichtingId);
        if (!levend) return;
        herkomst = t.data.herkomst;

        const frame = document.createElement('iframe');
        frame.setAttribute('aria-hidden', 'true');
        frame.setAttribute('title', 'Documentgenerator');
        /* Twee bij twee pixels, rechtsonder, achter de donkere achtergrond van
           de modal. Dat is met opzet: een iframe dat buiten het venster staat
           krijgt van de browser geen requestAnimationFrame meer, en daar wacht
           OpenLayers nu juist op -- met left:-10000px kwam er negentig
           seconden lang niets en liep de kaart in zijn timeout. Zichtbaar
           binnen het venster dus, maar zo klein en zo bedekt dat niemand het
           ziet. (De generator heeft daarnaast een eigen pomp die niet van rAF
           afhangt; zie renderKaartCluster in het meldingsformulier.)
           display:none of visibility:hidden kan hier om dezelfde reden niet. */
        frame.style.cssText = 'position:fixed; right:0; bottom:0; width:2px; height:2px; border:0; pointer-events:none; z-index:0;';
        frame.src = `${t.data.url}&ouder=${encodeURIComponent(window.location.origin)}`;
        document.body.appendChild(frame);
        frameRef.current = frame;

        klokRef.current = setTimeout(() => {
          if (!levend) return;
          setFout('Het document werd niet op tijd geleverd. Probeer het opnieuw; blijft het misgaan, dan is de kaartservice waarschijnlijk niet bereikbaar.');
          if (frameRef.current) { frameRef.current.remove(); frameRef.current = null; }
        }, TIJDSLOT_MS);
      } catch (e) {
        if (levend) setFout(leesbareFout(e));
      }
    })();

    return () => {
      levend = false;
      window.removeEventListener('message', opBericht);
      ruimOp();
    };
  }, [inrichtingId, doc, ruimOp]);

  // De melding "Gekopieerd" hoort weer te verdwijnen, anders lijkt een tweede
  // klik niets te doen.
  useEffect(() => {
    if (!gekopieerd) return undefined;
    const t = setTimeout(() => setGekopieerd(null), 2500);
    return () => clearTimeout(t);
  }, [gekopieerd]);

  const linkdata = { label: doc?.linklabel || 'digitale meldingsformulier' };

  const kopieerMail = async () => {
    const ok = await kopieerRijk(tekst, linkdata);
    setGekopieerd(ok ? 'mail' : 'mislukt');
  };

  const kopieerOnderwerp = async () => {
    const ok = await kopieerPlat(doc.onderwerp);
    setGekopieerd(ok ? 'onderwerp' : 'mislukt');
  };

  if (doc && !doc.kan) {
    return (
      <>
        <p className="meldoc-meta" style={{ marginTop: 0 }}>
          Er kan voor deze inrichting geen document gemaakt worden:
        </p>
        <ul className="meldoc-redenen">
          {(doc.redenen || []).map((r, i) => <li key={i}>{r}</li>)}
        </ul>
        <div className="meldoc-voet">
          <button className="btn btn-ghost" onClick={onClose}>Sluiten</button>
        </div>
      </>
    );
  }

  return (
    <>
      {fout && <div className="alert alert-error" style={{ marginTop: 0 }}>{fout}</div>}

      {!fout && !pdf && <Voortgang fase={fase} fractie={fractie} />}

      {pdf && (
        <a className="meldoc-bestand meldoc-bestand-knop" href={pdf.url} download={pdf.naam}>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="meldoc-bestand-icoon">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
          </svg>
          <span>
            <span className="meldoc-bestand-naam">{pdf.naam}</span>
            <span className="meldoc-bestand-maat">{maat(pdf.bytes)} · downloaden</span>
          </span>
          <svg viewBox="0 0 24 24" aria-hidden="true" className="meldoc-pijl-dl">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </a>
      )}

      {doc && doc.onderwerp && (
        <div className="publink-blok" style={{ marginTop: 22 }}>
          <div className="publink-kop">
            <h3 className="publink-titel">Onderwerp</h3>
            <button className="btn btn-ghost btn-sm" onClick={kopieerOnderwerp}>
              {gekopieerd === 'onderwerp' ? 'Gekopieerd' : 'Kopiëren'}
            </button>
          </div>
          <input className="input" readOnly value={doc.onderwerp}
                 onFocus={(e) => e.target.select()} />
        </div>
      )}

      {tekst && (
        <div className="publink-blok" style={{ marginTop: 0 }}>
          <div className="publink-kop">
            <h3 className="publink-titel">Mailtekst</h3>
            <div className="row" style={{ gap: 6 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setBewerken((b) => !b)}>
                {bewerken ? 'Voorbeeld' : 'Aanpassen'}
              </button>
              <button className="btn btn-primary btn-sm" onClick={kopieerMail}>
                {gekopieerd === 'mail' ? 'Gekopieerd' : 'Kopiëren'}
              </button>
            </div>
          </div>

          {bewerken ? (
            <>
              <textarea className="publink-tekstvak" value={tekst}
                        onChange={(e) => setTekst(e.target.value)} />
              <p className="publink-meta">
                Een lege regel begint een nieuw blok. Heeft een blok meer dan één
                regel, dan wordt de eerste regel de vette kop. De link wordt in de
                mail “{linkdata.label}”.
              </p>
            </>
          ) : (
            <>
              <MailVoorbeeld tekst={tekst} data={linkdata} />
              <p className="publink-meta">
                De tekst gaat als Aptos 11 mee naar Outlook. Je handtekening zet
                Outlook er zelf onder.
              </p>
            </>
          )}

          {gekopieerd === 'mislukt' && (
            <p className="publink-meta" style={{ color: '#b91c1c' }}>
              Kopiëren lukte niet. Selecteer de tekst en kopieer met Cmd+C.
            </p>
          )}
        </div>
      )}

      <div className="meldoc-voet">
        <button className="btn btn-ghost" onClick={onClose}>Sluiten</button>
      </div>
    </>
  );
}
